import {
    AfterViewInit,
    Directive,
    ElementRef,
    Host,
    NgZone,
    OnDestroy,
    OutputRefSubscription,
} from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

/**
 * Keeps two ng-select 5 behaviors that ng-select 23 dropped, for every
 * ng-select declared in a module that imports SharedModule:
 * - the remove icons of multi-select chips stay out of the tab order;
 * - an open dropdown also closes when a touch starts outside it (5.x closed
 *   on touchstart as well as mousedown, so scrolling away closed it).
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'ng-select',
    standalone: false,
})
export class LegacyNgSelectDirective implements AfterViewInit, OnDestroy {
    private observer: MutationObserver;
    private subscriptions: OutputRefSubscription[] = [];

    public constructor(
        private el: ElementRef<HTMLElement>,
        private zone: NgZone,
        @Host() private ngSelect: NgSelectComponent
    ) {}

    public ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            this.observer = new MutationObserver(() => this.hideChipIcons());
            this.observer.observe(this.el.nativeElement, {
                childList: true,
                subtree: true,
            });
        });
        this.hideChipIcons();

        this.subscriptions = [
            this.ngSelect.openEvent.subscribe(() =>
                this.zone.runOutsideAngular(() =>
                    document.addEventListener(
                        'touchstart',
                        this.closeOnOutsideTouch,
                        { capture: true, passive: true }
                    )
                )
            ),
            this.ngSelect.closeEvent.subscribe(() => this.stopTouchListener()),
        ];
    }

    public ngOnDestroy(): void {
        this.observer?.disconnect();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.stopTouchListener();
    }

    private readonly closeOnOutsideTouch = (event: TouchEvent): void => {
        const target = event.target as Element;
        // Only one panel is open at a time, and appendTo moves it out of the host.
        if (
            this.el.nativeElement.contains(target) ||
            target.closest?.('ng-dropdown-panel')
        ) {
            return;
        }
        this.zone.run(() => this.ngSelect.close());
    };

    private stopTouchListener(): void {
        document.removeEventListener('touchstart', this.closeOnOutsideTouch, {
            capture: true,
        });
    }

    private hideChipIcons(): void {
        this.el.nativeElement
            .querySelectorAll('.ng-value-icon[tabindex]')
            .forEach(icon => {
                icon.removeAttribute('tabindex');
                icon.setAttribute('aria-hidden', 'true');
            });
    }
}
